import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react'
import type {
  IFilterData,
  IFilterDataArray,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import { canSkipOnRowSelected } from 'helpers'
import { UserAttributes } from 'api/administration/participants/types'
import { SortValue } from 'models/rankmiTable.model'
import listActiveParticipants, {
  UsersSearchParams,
} from 'api/administration/participants/ListActiveParticipants'
import UsersTable from 'components/configuration/components/lms-profiles/UsersTable'

interface TableProps {
  users: number[]
  onRemove: (userId: number) => void
  selected: number[]
  setSelected: React.Dispatch<React.SetStateAction<number[]>>
  actions?: ReactNode
}

const Table = ({
  users,
  onRemove,
  selected,
  setSelected,
  actions,
}: TableProps) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortValue>()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const selectedUsersMap = useMemo(() => new Set(selected), [selected])

  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<UserAttributes[]>([])
  const [total, setTotal] = useState(0)

  const [filterData, setFilterData] = useState<IFilterData>()

  useEffect(() => {
    setPage(1)
  }, [users])

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'id',
        condition: 'and',
        operator: 'in',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: [...users.map((user) => user)],
      },
    ],
    [users]
  )

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      ...filterData,
      data: [...(filterData?.data || []), ...(filter || [])],
    }),
    [filterData, filter]
  )

  const params = useMemo(
    (): Omit<UsersSearchParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      usersFilter: filterDataMerge,
    }),
    [search, sort, filterDataMerge]
  )

  useEffect(() => {
    if (!users.length) {
      setList([])
      setTotal(0)
      return
    }
    setLoading(true)
    listActiveParticipants({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        setList(data)
        setTotal(total)
      })
      .finally(() => setLoading(false))
  }, [users, page, params, perPage])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleAllSelected = useCallback(async () => {
    setSelected(users)
  }, [setSelected, users])

  const handleAllSelectedRef = useRef(handleAllSelected)

  useEffect(() => {
    handleAllSelectedRef.current = handleAllSelected
  }, [handleAllSelected])

  const onRemoveUser = useCallback(
    (participant: UserAttributes): void => {
      const newSelected = new Set(selected)
      newSelected.delete(participant.id)
      setSelected(Array.from(newSelected))
      onRemove(participant.id)
    },
    [onRemove, selected, setSelected]
  )
  return (
    <div className="position-relative">
      <UsersTable
        data={{
          list,
          total,
          loading,
        }}
        onRemove={onRemoveUser}
        onRowSelected={({ selectedRows, selectedRowKeys, dispatcher }) => {
          if (canSkipOnRowSelected(dispatcher)) return

          if (dispatcher === 'handleUnselectAllRows') {
            setSelected([])
            return
          }
          const newUsers = selectedRows.filter(
            ({ id }) => !selectedUsersMap.has(id)
          ) as UserAttributes[]

          const keysSet = new Set(selectedRowKeys)

          const deletedUsers = users.filter((id) => !keysSet.has(id))

          // Rankmi-table has a bug, use onAllRowKeys how reference
          if (newUsers.length) {
            setSelected((prev) => [...prev, ...newUsers.map((user) => user.id)])
          }

          if (deletedUsers.length) {
            setSelected((prev) => {
              const deletedUsersMap = new Set(deletedUsers)
              return prev.filter((id) => !deletedUsersMap.has(id))
            })
          }
        }}
        selected={selected}
        onAllRowKeys={handleAllSelected}
        paginationConfig={{
          setSort,
          page,
          perPage,
          onChangeFilterData: setFilterData,
          setPage,
          setPerPage,
          filterData,
          search,
          onSearch: handleSearch,
        }}
        actions={actions}
      />
    </div>
  )
}

export default Table
